import React from 'react';
import RadioField from 'components/pure/utils/form/RadioField';
import CoachingPlanDescription from './CoachingPlanDescription';
import CoachingSkillVelocityChartContainer from 'components/coaching/widgets/CoachingSkillVelocityChartContainer';
import './CoachingPlanOverview.styl';
import CoachingReviewSelector from 'components/coaching/CoachingReviewSelector';
import CoachingPlanOverviewLegend from 'components/coaching/widgets/CoachingPlanOverviewLegend';
import connectToStores from 'alt-utils/lib/connectToStores';
import SkillStore from 'stores/SkillStore';
import DirectReportsStore from 'stores/coaching/DirectReportsStore';
import CoachingReviewStore from 'stores/coaching/CoachingReviewStore';
import CoachingStatsStore from 'stores/coaching/CoachingStatsStore';
import CoachingStatsActions from 'actions/coaching/CoachingStatsActions';
import RadioGroup from '@components/radio-group';

import {featureFlags} from 'utils/const';
import hasFlag from 'utils/hasFlag';

import CoachingGoalsView from './coaching-goals-view';
import KpiObservationStore from '@store/kpi/KpiObservationStore';
import KpiObservationActions from '@store/kpi/KpiObservationActions';
import KpiGoalActions from '@store/kpi/KpiTargetActions';
import {List} from 'immutable';
import WebDataButton from 'components/utils/WebDataButton';
import WebDataRocksActions, {WebDataType} from '@store/web-data-rocks/WebDataRocksActions';
import CoachingPlanSource from 'sources/coaching/CoachingPlanSource';
import Tooltip from '@material-ui/core/Tooltip';
import GetAppIcon from '@material-ui/icons/GetApp';
import ExcelSVG from 'images/icon/ExcelIcon.svg';
import AuthStore from 'stores/AuthStore';

const GRAPH_TYPE = {
  PROGRESS: 'progress',
  GOALS: 'goals',
};

@connectToStores
export default class CoachingPlanOverview extends React.PureComponent {
  static getStores() {
    return [
      DirectReportsStore,
      SkillStore,
      CoachingReviewStore,
      CoachingStatsStore,
      KpiObservationStore,
      AuthStore,
    ];
  }

  static getPropsFromStores(props) {
    const {planVelocitiesLoading, planVelocities, planVelocitiesError} =
      DirectReportsStore.getState();

    const {skillsByTeamList, areSkillsFetchingByTeam, wasErrorFetchingSkillsByTeam} =
      SkillStore.getState();

    const {currentlyFillingOutReviewId} = CoachingReviewStore.getState();

    const observations = KpiObservationStore.getState().kpiObservationsByUser.get(
      props.repId,
      List()
    );

    const {user} = AuthStore.getState();

    return {
      observations,
      planVelocitiesLoading,
      planVelocities,
      planVelocitiesError,
      skillsByTeamList,
      areSkillsFetchingByTeam,
      wasErrorFetchingSkillsByTeam,
      currentlyFillingOutReviewId,
      user,
      ...CoachingStatsStore.getState(),
    };
  }

  constructor(props) {
    super(props);
    this.state = {
      graphType: GRAPH_TYPE.PROGRESS,
    };
  }

  componentDidMount() {
    const {repId, coachingPlan} = this.props;
    CoachingStatsActions.coachingStats.defer(coachingPlan.id);
    KpiObservationActions.search.defer({
      userId: repId,
      startDate: coachingPlan.created.clone().subtract(2, 'week'),
      endDate: coachingPlan.completedAt,
    });
    KpiGoalActions.fetchGoalKpiTree.defer();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.coachingPlan.id !== this.props.coachingPlan.id) {
      CoachingStatsActions.coachingStats.defer(nextProps.coachingPlan.id);
    }
  }

  handleChangeGraphType = graphType => {
    this.setState({graphType});
  };

  webDataOnClick = async () => {
    const {coachingPlan} = this.props;
    // Dialog needs to be visible before data is set, so we need to show, then defer the setData call.
    WebDataRocksActions.showDialog('Coaching Plan');
    CoachingPlanSource.export(coachingPlan.id).then(exportedData => {
      WebDataRocksActions.setData(WebDataType.COACHING_PLAN, exportedData);
    });
  };

  render() {
    const {
      observations,
      coachingPlan,
      reviewId,
      contentDetailId,
      skillsByTeamList,
      onSelectContentDetail,
      showPlanName,
      planVelocities,
      currentlyFillingOutReviewId,
      user,
    } = this.props;

    const planVelocity = planVelocities && planVelocities.find(v => v.planId === coachingPlan.id);
    const contentDetailsWithSkills = coachingPlan.contentDetails.map(detail => [
      skillsByTeamList.find(skill => skill.id === detail.skillId),
      detail,
    ]);
    const [skill, contentDetail] = contentDetailsWithSkills.find(
      ([_, detail]) => detail.id === contentDetailId
    );
    const reviewsOnVelocityChart = coachingPlan.reviews
      .sortBy(r => (r.completedAt ? r.completedAt : r.scheduleAt))
      .filter(r =>
        coachingPlan.completed
          ? r.status === 'COMPLETED'
          : r.status === 'COMPLETED' || r.status === 'SCHEDULED'
      );

    const {coachingStats, coachingStatsLoading} = this.props;

    const showGoalsChart =
      hasFlag(featureFlags.coachingChartForGoals) &&
      hasFlag(featureFlags.adminKpis) &&
      !observations.isEmpty();

    const showSkillRadio =
      contentDetailsWithSkills.size > 1 && this.state.graphType !== GRAPH_TYPE.GOALS;
    const planHasReviews = coachingPlan.reviews.count(r => !!r.completedAt) > 0;
    const exportEnabled = user.isManager() || user.is_coach;

    return (
      <div className="coaching-plan-overview">
        <div className="row">
          {!planHasReviews && exportEnabled && (
            <div className="left">
              <Tooltip title={'There is no coaching review to export'}>
                <div style={{filter: 'invert(50%)'}}>
                  <div className="spreadsheet-download-button pointer">
                    <GetAppIcon />
                    <img src={ExcelSVG} alt="Excel" />
                  </div>
                </div>
              </Tooltip>
            </div>
          )}
          {planHasReviews && exportEnabled && (
            <div className="left">
              <WebDataButton onClick={this.webDataOnClick} tooltip="Export" />
            </div>
          )}

          <div className="right">
            {showPlanName && <h3> Coaching plan: {coachingPlan.name}</h3>}
          </div>
        </div>
        <div className="coaching-plan-overview-header">
          {showSkillRadio && (
            <div className="skill-selector">
              <label>Show Skill</label>
              {contentDetailsWithSkills.map(([skill, detail]) => (
                <RadioField
                  key={detail.id}
                  label={skill ? skill.skill : detail.custom}
                  checked={contentDetailId === detail.id}
                  onClick={() => onSelectContentDetail(detail.id)}
                />
              ))}
            </div>
          )}
          {showGoalsChart && (
            <RadioGroup
              name="graphType"
              value={this.state.graphType}
              onChange={this.handleChangeGraphType}
              options={[
                {label: 'Review Progress', value: GRAPH_TYPE.PROGRESS},
                {label: 'Review KPIs', value: GRAPH_TYPE.GOALS},
              ]}
            />
          )}
        </div>
        <div className="coaching-overview-details">
          <div className="coaching-plan-overview-left-col">
            {
              {
                [GRAPH_TYPE.PROGRESS]: (
                  <>
                    <CoachingSkillVelocityChartContainer
                      coachingPlan={coachingPlan}
                      planVelocity={planVelocity}
                      skill={skill}
                      contentDetail={contentDetail}
                      currentReviewId={reviewId}
                      coachingStats={coachingStats}
                      coachingStatsLoading={coachingStatsLoading}
                    />
                    <CoachingReviewSelector
                      reviews={reviewsOnVelocityChart}
                      reviewId={reviewId}
                      planStatus={coachingPlan.status}
                      currentlyFillingOutReviewId={currentlyFillingOutReviewId}
                    />
                  </>
                ),
                [GRAPH_TYPE.GOALS]: (
                  <CoachingGoalsView
                    velocity={planVelocity}
                    plan={coachingPlan}
                    observations={observations}
                  />
                ),
              }[this.state.graphType]
            }
          </div>
          <div className="coaching-plan-overview-right-col">
            <CoachingPlanDescription
              coachingPlan={coachingPlan}
              skill={skill}
              contentDetail={contentDetail}
              planVelocity={planVelocity}
            />
            <div className="coaching-plan-overview-right-col-bottom">
              <CoachingPlanOverviewLegend
                recipient={coachingPlan.recipient.info}
                skill={skill}
                contentDetail={contentDetail}
                graphType={this.state.graphType}
              />
              {this.state.graphType === GRAPH_TYPE.PROGRESS && (
                <SelectedMeetingScore
                  skill={skill}
                  contentDetail={contentDetail}
                  reviewId={reviewId}
                  coachingStats={coachingStats}
                  coachingStatsLoading={coachingStatsLoading}
                  reviews={reviewsOnVelocityChart}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

function SelectedMeetingScore({
  skill,
  contentDetail,
  reviewId,
  coachingStats,
  coachingStatsLoading,
  reviews,
}) {
  if (coachingStatsLoading || !coachingStats || !coachingStats.chart[contentDetail.id]) return null;

  const skillName = skill?.skill ?? contentDetail?.skillName ?? contentDetail?.custom;

  // Get meeting label from completed reviews/meetings
  // since some reviews/meetings don't have stats.
  const completedMeetings = reviews.filter(r => r.status !== 'SCHEDULED');
  const selectedMeetingIndex =
    completedMeetings && completedMeetings.findIndex(r => r.id === reviewId);
  const selectedMeetingLabel =
    selectedMeetingIndex === 0 ? 'Kickoff' : `Meeting #${selectedMeetingIndex + 1}`;

  // Get score from stats.
  const selectedMeetingStats =
    coachingStats &&
    coachingStats.chart[contentDetail.id].reviewScores.find(r => r.reviewId === reviewId);
  const reviewScore = selectedMeetingStats ? selectedMeetingStats.score : -1;

  return (
    skillName &&
    reviewScore >= 0 && (
      <div className="coaching-plan-overall-score">
        {skillName}
        <h1>{reviewScore} %</h1>
        <div>{selectedMeetingLabel}</div>
      </div>
    )
  );
}
